import {Flexidate, spawnTstzSpan} from "../tstzspan.ts";
import {useMemo} from "react";
import {ColorPaletteProp} from "@mui/joy";
import {useFullSelf} from "./useSelf.ts";
import {z} from "zod/v4";
import {InfallibleUseResult, pendingUseResult} from "../util/hookutil.tsx";

export const AgentStatus = z.literal(["initial", "onDuty", "out", "onBreak"]);
export type AgentStatus = z.infer<typeof AgentStatus>;

const UseShiftInfoReturn = z.object({
    status: AgentStatus,
    lastShiftBegan: z.optional(Flexidate),
    lastShiftEnded: z.optional(Flexidate),
    lastBreakBegan: z.optional(Flexidate),
    lastBreakEnded: z.optional(Flexidate),
});
export type UseShiftInfoReturn = z.infer<typeof UseShiftInfoReturn>;

export function useSelfShiftInfo(): InfallibleUseResult<UseShiftInfoReturn> {
    const fstqrv = useFullSelf();
    const self = fstqrv.data;
    const ssi = useMemo(() => {
        if (self) {
            const wiredLastShift = self.lastShift;
            const wiredLastBreak = self.lastBreak;
            const lastShift = spawnTstzSpan(wiredLastShift)
            const lastBreak = spawnTstzSpan(wiredLastBreak)
            const irv: Partial<UseShiftInfoReturn> = {};
            if (lastShift.valid && lastShift.lower && lastShift.lowerBound === "[") {
                irv.lastShiftBegan = lastShift.lower;
                if (lastShift.upper && lastShift.upperBound === ")") {
                    irv.lastShiftEnded = lastShift.upper;
                }
            }
            if (lastBreak.valid && lastBreak.lower && lastBreak.lowerBound === "[") {
                irv.lastBreakBegan = lastBreak.lower;
                if (lastBreak.upper && lastBreak.upperBound === ")") {
                    irv.lastBreakEnded = lastBreak.upper;
                }
            }
            if (!irv.lastShiftBegan) {
                irv.status = "initial";
            } else if (irv.lastShiftEnded) {
                irv.status = "out";
            } else if (!irv.lastBreakBegan || !!irv.lastBreakEnded) {
                irv.status = "onDuty";
            } else {
                irv.status = "onBreak";
            }
            const rv = UseShiftInfoReturn.parse(irv);
            return rv;
        }
    }, [self]);

    if (fstqrv.isPending || !ssi) {
        return pendingUseResult;
    }

    return {...fstqrv, data: ssi};
}

export const statusPalettes: Record<AgentStatus, ColorPaletteProp> = {
    initial: "success",
    out: "neutral",
    onBreak: "warning",
    onDuty: "primary",
} as const;

export function useSelfWorkStatusPalette() {
    const tqrv = useSelfShiftInfo();
    const rv = {...tqrv, data: tqrv.data ? statusPalettes[tqrv.data.status] : undefined};
    return rv;
}

